import { useEffect, useState } from 'react';
import {
  Modal, Stack, TextInput, Autocomplete, Select, Group, Button, Alert, Paper, Table, ScrollArea, Text, Loader, Center, Title, Code, CopyButton, Badge, Avatar,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import { api } from '../../api/client';
import { initialsOf } from '../../lib/initials';

export interface AdminUser {
  id: number;
  username: string;
  role: 'admin' | 'editor' | 'viewer';
  displayName: string | null;
  mustChangePassword: boolean;
  technicianId: number | null;
  technicianName: string | null;
  lastLoginAt: string | null;
  loginCount: number;
  entraOid?: string | null;
  entraLinked?: boolean;
  imagePath?: string | null;
  status?: 'active' | 'pending';
  pending?: boolean;
}

interface LoginRow { id: number; at: string; ip: string | null }

interface DirectoryUser { oid: string; displayName: string; email: string; upn: string }
type LookupReason = 'not_configured' | 'unavailable' | 'insufficient_privileges' | null;

export default function UserModal({
  initial,
  onClose,
  onDone,
  technicians,
}: {
  initial: AdminUser | null;
  onClose: () => void;
  onDone: () => void;
  technicians: Array<{ id: number; name: string }>;
}) {
  const isEdit = !!initial;
  const [username, setUsername] = useState(initial?.username ?? '');
  const [displayName, setDisplayName] = useState(initial?.displayName ?? '');
  const [role, setRole] = useState<'admin' | 'editor' | 'viewer'>(initial?.role ?? 'viewer');
  const [technicianId, setTechnicianId] = useState<string | null>(
    initial?.technicianId != null ? String(initial.technicianId) : null,
  );
  const [logins, setLogins] = useState<LoginRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [handoffPath, setHandoffPath] = useState<string | null>(null);
  const [createdEntraLinked, setCreatedEntraLinked] = useState(false);
  // True once the user has been created (whether or not a password was issued).
  // SSO-only users come back with no passwordFilePath, so we can't gate the
  // terminal modal state on handoffPath alone.
  const [submitted, setSubmitted] = useState(false);

  // --- Microsoft Entra directory lookup (add-mode only) ---
  // The username box doubles as a directory search: as the admin types a name
  // or email we query Graph and offer matches. The authoritative Entra link
  // (the immutable oid) is derived server-side from the chosen username at
  // create — the client never sends an oid. This state only drives the
  // "will be linked" hint. If the directory is unconfigured/unavailable, the
  // admin falls back to manual entry.
  const [entraMatchUpn, setEntraMatchUpn] = useState<string | null>(null);
  // Live preview of the picked directory user's Entra/Teams photo (loads async).
  const [previewPhotoUrl, setPreviewPhotoUrl] = useState<string | null>(null);
  // Autocomplete uses string items (the value is what lands in the input), so
  // the option value is the UPN/email; the name is shown via renderOption.
  const [options, setOptions] = useState<Array<{ value: string }>>([]);
  const [records, setRecords] = useState<Record<string, DirectoryUser>>({});
  const [lookupReason, setLookupReason] = useState<LookupReason>(null);
  const [searching, setSearching] = useState(false);
  const [debouncedUsername] = useDebouncedValue(username, 250);

  useEffect(() => {
    if (!initial) return;
    (async () => {
      try { setLogins(await api.get<LoginRow[]>(`/admin/users/${initial.id}/logins?limit=200`)); }
      catch { setLogins([]); }
    })();
  }, [initial]);

  useEffect(() => {
    if (isEdit) return;
    const q = debouncedUsername.trim();
    if (q.length < 2) { setOptions([]); setLookupReason(null); setSearching(false); return; }
    let cancelled = false;
    setSearching(true);
    api.get<{ available: boolean; reason?: string; users: DirectoryUser[] }>(
      `/admin/entra/users?q=${encodeURIComponent(q)}`,
    )
      .then((r) => {
        if (cancelled) return;
        if (!r.available) {
          setLookupReason((r.reason as LookupReason) ?? 'unavailable');
          setOptions([]); setRecords({});
          return;
        }
        setLookupReason(null);
        // Key by UPN (always unique) rather than mail (can be shared/null), so
        // every directory member is independently selectable.
        const recs: Record<string, DirectoryUser> = {};
        const opts: Array<{ value: string }> = [];
        for (const u of r.users) {
          recs[u.upn] = u;
          opts.push({ value: u.upn });
        }
        setRecords(recs); setOptions(opts);
      })
      .catch(() => { if (!cancelled) { setLookupReason('unavailable'); setOptions([]); } })
      .finally(() => { if (!cancelled) setSearching(false); });
    return () => { cancelled = true; };
  }, [debouncedUsername, isEdit]);

  function onUsernameChange(v: string) {
    setUsername(v);
    // Editing away from a picked Entra match clears the preview.
    if (entraMatchUpn && v.trim().toLowerCase() !== entraMatchUpn) {
      setEntraMatchUpn(null);
      setPreviewPhotoUrl(null);
    }
  }

  function onPickEntra(upn: string) {
    const rec = records[upn];
    if (!rec) return;
    setUsername(rec.upn);
    setDisplayName(rec.displayName || '');
    setEntraMatchUpn(rec.upn);
    // Load the person's Entra/Teams photo as a live preview (same photo that is
    // saved on create). 204 when there's no photo → Avatar shows initials.
    setPreviewPhotoUrl(`/api/v1/admin/entra/users/photo?id=${encodeURIComponent(rec.oid)}`);
    setErr(null);
  }

  async function save() {
    setErr(null);
    const u = username.trim().toLowerCase();
    if (!/^[^\s@]+@dotlumen\.com$/.test(u)) return setErr('Username must end in @dotlumen.com.');
    setBusy(true);
    try {
      const techId = technicianId ? Number(technicianId) : null;
      if (isEdit && initial) {
        await api.put(`/admin/users/${initial.id}`, {
          role,
          displayName: displayName.trim() || null,
          technicianId: techId,
        });
        notifications.show({ message: 'User updated.', color: 'green' });
        onDone();
      } else {
        // No oid is sent — the server derives the authoritative Entra oid from
        // the username via Graph and links the account itself.
        const created = await api.post<AdminUser & { passwordFilePath?: string; passwordFilename?: string }>(
          '/admin/users',
          { username: u, role, displayName: displayName.trim() || null, technicianId: techId },
        );
        setHandoffPath(created.passwordFilePath ?? null);
        setCreatedEntraLinked(!!created.entraLinked);
        setSubmitted(true);
        notifications.show({
          message: created.entraLinked ? `User ${created.username} linked to Microsoft Entra.` : `User ${created.username} created.`,
          color: 'green',
        });
        // Keep the modal open so the admin can see the result.
      }
    } catch (e: any) {
      setErr(e.message ?? 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  async function resetPassword() {
    if (!initial) return;
    setBusy(true);
    try {
      const r = await api.post<{ passwordFilePath: string; passwordFilename: string }>(
        `/admin/users/${initial.id}/reset-password`,
      );
      setHandoffPath(r.passwordFilePath);
      notifications.show({
        message: 'Password reset. Open the password file on the server to retrieve it.',
        color: 'orange',
      });
    } catch (e: any) {
      setErr(e.message ?? 'Reset failed');
    } finally { setBusy(false); }
  }

  function confirmDelete() {
    if (!initial) return;
    modals.openConfirmModal({
      title: `Delete ${initial.username}?`,
      children: (
        <Text size="sm">
          The user will be soft-deleted: their login history is preserved for auditing,
          and the username can be re-used in a future account if needed.
        </Text>
      ),
      labels: { confirm: 'Delete user', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await api.delete(`/admin/users/${initial.id}`);
          notifications.show({ message: 'User deleted.', color: 'gray' });
          onDone();
        } catch (e: any) {
          notifications.show({ message: e.message ?? 'Delete failed', color: 'red' });
        }
      },
    });
  }

  const entraMatched = !!entraMatchUpn && username.trim().toLowerCase() === entraMatchUpn;
  const showManualHint = !isEdit && !entraMatched && !submitted && username.trim().length >= 2;

  return (
    <Modal opened onClose={onClose} size="lg" title={<span className="modal-title-custom">{isEdit ? `Edit ${initial!.username}` : 'Add User'}</span>}>
      <Stack>
        {isEdit ? (
          <TextInput
            label="Username (e-mail)"
            value={username}
            disabled
          />
        ) : (
          <Autocomplete
            data-tour="user-username"
            label="Username (e-mail)"
            description="Type a name or email to search Microsoft Entra, then press Enter to select — or type a full @dotlumen.com address to add manually"
            placeholder="Start typing a name or email…"
            value={username}
            data={options}
            // Server already filtered; show its results verbatim (don't re-filter client-side).
            filter={({ options }) => options}
            // Option value is the email/UPN (what lands in the input); show the
            // person's name + email in the dropdown row.
            renderOption={({ option }) => {
              const rec = records[option.value];
              return (
                <div>
                  <Text size="sm">{rec?.displayName || option.value}</Text>
                  {rec?.displayName && <Text size="xs" c="dimmed">{rec.email}</Text>}
                </div>
              );
            }}
            onChange={onUsernameChange}
            onOptionSubmit={onPickEntra}
            rightSection={searching ? <Loader size="xs" /> : undefined}
            disabled={submitted}
            required
          />
        )}

        {/* Entra link / fallback callouts (add mode) */}
        {!isEdit && entraMatched && !submitted && (
          <Alert color="green" variant="light" title={<Group gap={6}><Badge color="green" variant="filled" size="sm">Microsoft Entra</Badge><span>Matched</span></Group>}>
            <Group gap="md" wrap="nowrap" align="center">
              <Avatar src={previewPhotoUrl ?? undefined} radius="xl" size={64} color="green">
                {initialsOf(displayName, username)}
              </Avatar>
              <div>
                <Text size="sm" fw={500}>{displayName || username}</Text>
                <Text size="xs" c="dimmed">{username}</Text>
                <Text size="xs" c="dimmed" mt={4}>
                  Signs in with Microsoft · name and photo are loaded from Entra and saved on create.
                </Text>
              </div>
            </Group>
          </Alert>
        )}
        {showManualHint && lookupReason === 'not_configured' && (
          <Alert color="blue" variant="light">
            Microsoft Entra lookup isn’t configured on this server — add the user manually with their
            <Code>@dotlumen.com</Code> address. They can still use SSO once it’s set up.
          </Alert>
        )}
        {showManualHint && lookupReason === 'unavailable' && (
          <Alert color="yellow" variant="light">
            Microsoft Entra directory lookup is unavailable right now — you can still add the user
            manually with their <Code>@dotlumen.com</Code> address.
          </Alert>
        )}
        {showManualHint && lookupReason === 'insufficient_privileges' && (
          <Alert color="yellow" variant="light" title="Microsoft Entra permission not granted">
            The app can reach Entra but lacks directory access. An admin must grant the{' '}
            <Code>User.Read.All</Code> <strong>application</strong> permission (with admin consent) on
            the CYCLOTRON app registration. You can add the user manually meanwhile.
          </Alert>
        )}
        {showManualHint && lookupReason === null && !searching && options.length === 0 && (
          <Text size="xs" c="dimmed">
            No Microsoft Entra match for “{username.trim()}” — this will be added as a manual account.
          </Text>
        )}

        <TextInput
          data-tour="user-display-name"
          label="Display name"
          value={displayName}
          onChange={(e) => setDisplayName(e.currentTarget.value)}
          placeholder="Jane Doe"
          disabled={submitted}
        />
        <Select
          data-tour="user-role"
          label="Access rights"
          data={['admin', 'editor', 'viewer']}
          value={role}
          onChange={(v) => v && setRole(v as 'admin' | 'editor' | 'viewer')}
          disabled={submitted}
          required
        />
        <Select
          label="Lab Technician (optional)"
          description="Link this account to a technician record for reporting purposes"
          placeholder="— none —"
          data={technicians.map((t) => ({ value: String(t.id), label: t.name }))}
          value={technicianId}
          onChange={setTechnicianId}
          disabled={submitted}
          clearable
          searchable
        />

        {submitted && createdEntraLinked && (
          <Alert color="green" variant="light" title="Linked to Microsoft Entra">
            <Text size="sm">
              This user signs in with <strong>Microsoft</strong> only. <strong>No password is created</strong>
              and nothing is written to the server — password login is permanently disabled for this account.
            </Text>
          </Alert>
        )}

        {handoffPath && (
          <Alert color="orange" title="One-time password — retrieve from the server">
            <Stack gap={6}>
              <Text size="sm">
                The password has been written to a protected file on the server.
                Open the file <strong>directly on the server machine</strong>, read the{' '}
                <code>password:</code> line, and share it with the user in person. The password
                is never transmitted over the network. The user will be required to change it on
                first login.
              </Text>
              <Group gap={6} wrap="wrap">
                <Code>{handoffPath}</Code>
                <CopyButton value={handoffPath}>
                  {({ copied, copy }) => (
                    <Button size="compact-xs" variant="light" onClick={copy}>
                      {copied ? 'Copied' : 'Copy path'}
                    </Button>
                  )}
                </CopyButton>
              </Group>
              <Text size="xs" c="dimmed">
                File permissions are <code>0600</code> (owner-read only). The file is
                overwritten the next time a password is generated for this user.
              </Text>
            </Stack>
          </Alert>
        )}

        {isEdit && (
          <>
            <Group justify="space-between" align="center">
              <Group gap="xs">
                <Title order={5}>Login history</Title>
                {initial!.entraLinked && <Badge color="blue" variant="light" size="sm">Microsoft Entra</Badge>}
              </Group>
              {initial!.lastLoginAt && <Text size="xs" c="dimmed">Last: {dayjs(initial!.lastLoginAt).format('YYYY-MM-DD HH:mm')} · total: {initial!.loginCount}</Text>}
            </Group>
            <Paper withBorder>
              <ScrollArea.Autosize mah={240}>
                {!logins ? (
                  <Center p="md"><Loader size="sm" /></Center>
                ) : logins.length === 0 ? (
                  <Text c="dimmed" size="sm" p="sm">No logins recorded yet.</Text>
                ) : (
                  <Table>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Date / Hour</Table.Th>
                        <Table.Th>IP</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {logins.map((l) => (
                        <Table.Tr key={l.id}>
                          <Table.Td>{dayjs(l.at).format('YYYY-MM-DD HH:mm:ss')}</Table.Td>
                          <Table.Td>{l.ip ?? '—'}</Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                )}
              </ScrollArea.Autosize>
            </Paper>
          </>
        )}

        {err && <Alert color="red">{err}</Alert>}

        <Group justify="space-between" mt="md">
          <Group gap="xs">
            {isEdit && (
              <>
                <Button variant="light" color="orange" onClick={resetPassword} loading={busy}>Reset password</Button>
                <Button variant="light" color="red" onClick={confirmDelete}>Delete user</Button>
              </>
            )}
          </Group>
          <Group>
            <Button data-tour="user-close" variant="default" onClick={onClose}>Close</Button>
            {submitted && !isEdit ? (
              <Button color="green" onClick={onDone}>Done — Close</Button>
            ) : (
              <Button data-tour="user-create" onClick={save} loading={busy}>{isEdit ? 'Save' : 'Create'}</Button>
            )}
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
